<template>
  <header class="w-full">
    <!-- Main Navbar -->
    <nav class="bg-grey-light-950 shadow-sm relative">
      <div class="max-w-content mx-auto w-full max-sm:px-6 max-xl:px-13">
        <div class="flex items-center h-20">
          <!-- Logo -->
          <router-link to="/business" class="flex items-center min-w-[97px]">
            <img :src="layupLogo" alt="Layup Logo" class="h-[25px] w-auto" />
          </router-link>

          <!-- Desktop Navigation -->
          <div class="hidden lg:flex w-full">
            <div class="flex items-center space-x-8 ml-12">
              <!-- Products Dropdown -->
              <div class="relative" @mouseenter="openDropdown('products')" @mouseleave="closeDropdown('products')">
                <button
                  class="flex items-center gap-2 text-grey-light-400 font-semibold hover:text-base-white hover:cursor-pointer transition-colors"
                >
                  Products
                  <ChevronDown class="w-4 h-4" />
                </button>
                <transition name="dropdown">
                  <div
                    v-if="activeDropdown === 'products'"
                    class="absolute top-full left-0 mt-2 bg-brand-800 rounded-xl custom-shadow z-50 flex overflow-hidden"
                  >
                    <!-- Column 1: Core Products -->
                    <div class="flex flex-col gap-2 px-5 py-6 w-72">
                      <div class="px-3 py-3">
                        <h3 class="text-green-200 text-2xl font-semibold leading-8">LayUp Products</h3>
                      </div>

                      <router-link
                        v-for="(item, index) in productsColumnOne"
                        :key="item.name"
                        v-show="index < 3"
                        :to="item.to"
                        class="px-3 py-3 rounded-lg hover-green-bg transition-colors group"
                      >
                        <div
                          class="font-semibold text-base text-white group-hover:text-green-200 leading-6 transition-colors"
                        >
                          {{ item.name }}
                        </div>
                        <div
                          class="text-sm text-grey-light-400 group-hover:text-green-200 mt-1 leading-5 transition-colors"
                        >
                          {{ item.description }}
                        </div>
                      </router-link>
                    </div>

                    <!-- Column 2: Additional Products -->
                    <div v-if="productsColumnTwo.length > 0" class="flex flex-col gap-2 px-5 py-6 w-[287px]">
                      <!-- Spacer to align with other columns -->
                      <div class="px-3 py-3 opacity-0">
                        <h3 class="text-green-200 text-2xl font-semibold leading-8">LayUp Products</h3>
                      </div>

                      <router-link
                        v-for="item in productsColumnTwo"
                        :key="item.name"
                        :to="item.to"
                        class="px-3 py-3 rounded-lg hover-green-bg transition-colors group"
                      >
                        <div
                          class="font-semibold text-base leading-6 text-white group-hover:text-green-200 transition-colors"
                        >
                          {{ item.name }}
                        </div>
                        <div
                          class="text-sm mt-1 leading-5 text-grey-light-400 group-hover:text-green-200 transition-colors"
                        >
                          {{ item.description }}
                        </div>
                      </router-link>
                    </div>

                    <!-- Column 3: Image/CTA -->
                    <div class="flex flex-col items-center justify-center pl-5 pr-10 py-5">
                      <div class="w-[459px] h-72 rounded-[15px] overflow-hidden relative">
                        <img
                          :src="productsImage"
                          alt="LayUp Payment Options"
                          class="w-full h-full object-cover rounded-[15px]"
                        />
                        <div class="absolute bottom-7 left-7">
                          <router-link :to="{ name: 'business-enquiry' }">
                            <Button
                              class="bg-brand-600 hover:bg-[#0c6673] border border-brand-600 text-white font-semibold px-3 py-[7px] rounded-md shadow-sm text-[11.44px] leading-[17px]"
                            >
                              Talk to sales
                            </Button>
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>

              <!-- Integrations Dropdown -->
              <div
                class="relative"
                @mouseenter="openDropdown('integrations')"
                @mouseleave="closeDropdown('integrations')"
              >
                <button
                  class="flex items-center gap-2 text-grey-light-400 font-semibold hover:text-base-white hover:cursor-pointer transition-colors"
                >
                  Integrations
                  <ChevronDown class="w-4 h-4" />
                </button>
                <transition name="dropdown">
                  <div
                    v-if="activeDropdown === 'integrations'"
                    class="absolute top-full left-0 mt-2 bg-brand-800 rounded-xl custom-shadow z-50 flex overflow-hidden"
                  >
                    <!-- Column 1: For Developers -->
                    <div class="flex flex-col gap-2 px-5 py-6 w-[287px]">
                      <div class="px-3 py-3">
                        <h3 class="text-green-200 text-2xl font-semibold leading-8">For Developers</h3>
                      </div>
                      <a
                        v-for="item in integrationsColumnOne"
                        :key="item.name"
                        :href="item.to"
                        class="px-3 py-3 rounded-lg hover-green-bg transition-colors group"
                        @click="isMobileMenuOpen = false"
                      >
                        <div
                          class="font-semibold text-base text-white group-hover:text-green-200 leading-6 transition-colors"
                        >
                          {{ item.name }}
                        </div>
                        <div
                          class="text-sm text-grey-light-400 group-hover:text-green-200 mt-1 leading-5 transition-colors"
                        >
                          {{ item.description }}
                        </div>
                      </a>
                    </div>

                    <div class="flex flex-col gap-2 px-5 py-6 w-[287px]">
                      <div class="px-3 py-3">
                        <h3 class="text-green-200 text-2xl font-semibold leading-8">LayUp Plugins</h3>
                      </div>

                      <router-link
                        v-for="item in integrationsColumnTwo"
                        :key="item.name"
                        :to="item.to"
                        class="px-3 py-3 rounded-lg hover-green-bg transition-colors group"
                      >
                        <div
                          class="font-semibold text-base text-white group-hover:text-green-200 leading-6 transition-colors"
                        >
                          {{ item.name }}
                        </div>
                        <div
                          class="text-sm text-grey-light-400 group-hover:text-green-200 mt-1 leading-5 transition-colors"
                        >
                          {{ item.description }}
                        </div>
                      </router-link>
                    </div>

                    <!-- Column 3: Image/CTA -->
                    <div class="flex flex-col items-center justify-center pr-10 py-5">
                      <div class="w-[459px] h-72 rounded-[15px] overflow-hidden relative">
                        <img
                          :src="integrationsImage"
                          alt="LayUp Integrations"
                          class="w-full h-full object-cover rounded-[15px]"
                        />
                        <div class="absolute bottom-7 left-7">
                          <Button
                            class="px-3 py-[7px] rounded-md shadow-sm text-[11.44px] leading-[17px]"
                            @click="router.push({ name: 'business-enquiry' })"
                          >
                            Get Started
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>

              <!-- Pricing Link -->
              <router-link
                to="/business/layby/pricing"
                class="text-grey-light-400 font-semibold hover:text-base-white transition-colors"
              >
                Pricing
              </router-link>

              <!-- Resources Dropdown -->
              <div class="relative" @mouseenter="openDropdown('resources')" @mouseleave="closeDropdown('resources')">
                <button
                  class="flex items-center gap-2 text-grey-light-400 font-semibold hover:text-base-white hover:cursor-pointer transition-colors"
                >
                  Resources
                  <ChevronDown class="w-4 h-4" />
                </button>
                <transition name="dropdown">
                  <div
                    v-if="activeDropdown === 'resources'"
                    class="absolute top-full left-0 mt-2 bg-brand-800 rounded-xl custom-shadow z-50 overflow-hidden"
                  >
                    <div class="flex flex-col gap-2 px-5 py-6">
                      <div class="px-3 py-3">
                        <h3 class="text-green-200 text-2xl font-semibold leading-8">Important Links</h3>
                      </div>

                      <router-link
                        v-for="item in resourcesDropdown"
                        :key="item.name"
                        :to="item.to"
                        class="px-3 py-3 rounded-lg hover-green-bg transition-colors group w-80"
                      >
                        <div
                          class="font-semibold text-base text-white group-hover:text-green-200 leading-6 transition-colors"
                        >
                          {{ item.name }}
                        </div>
                        <div
                          class="text-sm text-grey-light-400 group-hover:text-green-200 mt-1 leading-5 transition-colors"
                        >
                          {{ item.description }}
                        </div>
                      </router-link>
                    </div>
                  </div>
                </transition>
              </div>
            </div>

            <div class="ml-auto flex gap-2">
              <router-link :to="{ name: 'business-enquiry' }">
                <Button variant="outline-light" class="text-base">Talk To Sales</Button>
              </router-link>
              <a :href="signInUrl">
                <Button>Sign In</Button>
              </a>
            </div>
          </div>

          <!-- Mobile Menu Button -->
          <div class="lg:hidden ml-auto">
            <Button variant="ghost" @click="toggleMobileMenu">
              <Menu v-if="!isMobileMenuOpen" class="size-7" />
              <X v-if="isMobileMenuOpen" class="size-7" />
            </Button>
          </div>
        </div>
      </div>

      <!-- Mobile Menu -->
      <transition @before-enter="beforeEnter" @enter="enter" @leave="leave">
        <div
          v-if="isMobileMenuOpen"
          class="absolute overflow-hidden left-0 right-0 top-full bg-grey-light-900 shadow-lg py-6 z-50 lg:hidden border-t border-grey-light-800"
        >
          <div class="flex flex-col gap-2">
            <!-- Mobile Products -->
            <div class="border-b border-grey-light-800 pb-2">
              <button
                @click="toggleMobileDropdown('products')"
                class="w-full flex justify-between items-center text-base-white font-semibold px-4 py-3 hover:cursor-pointer"
              >
                Products
                <ChevronDown
                  class="w-5 h-5 text-grey-light-400"
                  :class="{ 'rotate-180': mobileActiveDropdown === 'products' }"
                />
              </button>
              <div v-if="mobileActiveDropdown === 'products'" class="flex flex-col">
                <router-link
                  v-for="item in productsDropdown"
                  :key="item.name"
                  :to="item.to"
                  class="px-8 py-1.5 text-base text-base-white hover:bg-grey-light-800 transition-colors"
                  @click="isMobileMenuOpen = false"
                >
                  {{ item.name }}
                </router-link>
              </div>
            </div>

            <!-- Mobile Integrations -->
            <div class="border-b border-grey-light-800 pb-2">
              <button
                @click="toggleMobileDropdown('integrations')"
                class="w-full flex justify-between items-center text-base-white font-semibold px-4 py-3 hover:cursor-pointer"
              >
                Integrations
                <ChevronDown
                  class="w-5 h-5 text-grey-light-400"
                  :class="{ 'rotate-180': mobileActiveDropdown === 'integrations' }"
                />
              </button>
              <div v-if="mobileActiveDropdown === 'integrations'" class="flex flex-col">
                <a
                  v-for="item in integrationsColumnOne"
                  :key="item.name"
                  :href="item.to"
                  class="px-8 py-1.5 text-base text-base-white hover:bg-grey-light-800 transition-colors"
                  @click="isMobileMenuOpen = false"
                >
                  {{ item.name }}
                </a>
                <a
                  v-for="item in integrationsColumnTwo"
                  :key="item.name"
                  :href="item.to"
                  class="px-8 py-1.5 text-base text-base-white hover:bg-grey-light-800 transition-colors"
                  @click="isMobileMenuOpen = false"
                >
                  {{ item.name }}
                </a>
              </div>
            </div>

            <!-- Mobile Pricing -->
            <div class="border-b border-grey-light-800 pb-2">
              <router-link
                to="/business/pricing"
                class="block text-base-white font-semibold px-4 py-3"
                @click="isMobileMenuOpen = false"
              >
                Pricing
              </router-link>
            </div>

            <!-- Mobile Resources -->
            <div class="pb-2">
              <button
                @click="toggleMobileDropdown('resources')"
                class="w-full flex justify-between items-center text-base-white font-semibold px-4 py-3 hover:cursor-pointer"
              >
                Resources
                <ChevronDown
                  class="w-5 h-5 text-grey-light-400"
                  :class="{ 'rotate-180': mobileActiveDropdown === 'resources' }"
                />
              </button>
              <div v-if="mobileActiveDropdown === 'resources'" class="flex flex-col">
                <router-link
                  v-for="item in resourcesDropdown"
                  :key="item.name"
                  :to="item.to"
                  class="px-8 py-1.5 text-base text-base-white hover:bg-grey-light-800 transition-colors"
                  @click="isMobileMenuOpen = false"
                >
                  {{ item.name }}
                </router-link>
              </div>
            </div>
          </div>

          <!-- Mobile Actions -->
          <div class="flex flex-col gap-3 px-4 pt-6 mt-2">
            <a :href="signInUrl">
              <Button variant="outline" class="w-full text-base">Sign In</Button>
            </a>
            <router-link :to="{ name: 'business-enquiry' }" @click="isMobileMenuOpen = false">
              <Button class="w-full">Get in Touch</Button>
            </router-link>
          </div>
        </div>
      </transition>
    </nav>
  </header>
</template>

<script setup lang="ts">
import integrationsImage from '@/assets/images/business/integrations-dropdown.webp';
import layupLogo from '@/assets/images/business/layup-logo-white.svg';
import productsImage from '@/assets/images/business/products-dropdown.png';
import { Button } from '@/components/ui/button';
import { beforeEnter, enter, leave } from '@/utils/animations';
import { ChevronDown, Menu, X } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { DASHBOARDS_URL } from '../../config';

const router = useRouter();
const isMobileMenuOpen = ref<boolean>(false);
const activeDropdown = ref<string | null>(null);
const mobileActiveDropdown = ref<string | null>(null);
const closeTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

type Product = {
  name: string;
  description: string;
  to: string;
  highlighted?: boolean;
};

const productsColumnOne: Product[] = [
  {
    name: 'Lay-by',
    description: 'Flexible, interest-free instalments designed for businesses of any size.',
    to: '/business/layby',
  },
  {
    name: 'Save now, buy later',
    description: 'Customers save gradually and unlock purchases debt-free.',
    to: '/business/save-now-buy-later',
  },
  {
    name: 'Payment plans',
    description: "Offer easy, structured payments that fit your customers' budgets.",
    to: '/business/merchant-payment-plan',
  },
];

const productsColumnTwo: Product[] = [
  /* Wait for these products to be ready
  {
    name: 'Smart subscriptions',
    description: 'Automate recurring payments to grow customer loyalty and revenue.',
    to: '/business/subscriptions',
    highlighted: false,
  },
  {
    name: 'Stokvels',
    description: 'Empower group savings with secure, trackable payment plans.',
    to: '/business/stokvels',
    highlighted: true,
  },
  {
    name: 'Pre-orders',
    description: 'Secure sales upfront and let customers pay over time.',
    to: '/business/pre-orders',
    highlighted: false,
  },
  */
];

const productsDropdown = [...productsColumnOne, ...productsColumnTwo];

const integrationsColumnOne = [
  {
    name: 'Integrations',
    description: 'Build custom integrations tailored to your business.',
    to: '/business/integrations',
  },
  {
    name: 'API documentation',
    description: 'Access everything you need to create seamless solutions.',
    to: 'https://documentation.layup.co.za/',
  },
];

const integrationsColumnTwo = [
  {
    name: 'Point of sale',
    description: 'Enable LayUp directly at checkout with POS integration.',
    to: '/business/pos',
  },
  {
    name: 'Shopify integrations',
    description: 'Add LayUp to your Shopify store in minutes.',
    to: '/business/plugin/shopify',
  },
  {
    name: 'WooCommerce integrations',
    description: 'Offer flexible payment plans on your WooCommerce site.',
    to: '/business/plugin/woocommerce',
  },
  {
    name: 'Magento integrations',
    description: "Scale sales with LayUp's Magento plugin.",
    to: '/business/plugin/magento',
  },
];

const resourcesDropdown = [
  {
    name: 'Business FAQs',
    description: 'Most asked questions',
    to: '/business/faq',
  },
  {
    name: 'Blog',
    description: 'News & articles',
    to: '/business/blog',
  },
  /* LAYUP-3363
  {
    name: 'Careers',
    description: 'See open positions',
    to: '/business/careers',
  },
  */
  /* LAYUP-4525
  {
    name: 'Brand resources',
    description: 'Downloadable brand assets',
    to: '/business/brand-resources',
  },
  */
];

// These 2 functions are used to handle the dropdown hover behavior with a slight delay on close to make it not flash
const openDropdown = (dropdown: string) => {
  if (closeTimeout.value) {
    clearTimeout(closeTimeout.value);
    closeTimeout.value = null;
  }
  activeDropdown.value = dropdown;
};

const closeDropdown = (dropdown: string) => {
  closeTimeout.value = setTimeout(() => {
    if (activeDropdown.value === dropdown) {
      activeDropdown.value = null;
    }
  }, 75);
};

const toggleMobileMenu = (): void => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
  mobileActiveDropdown.value = null;
};

const toggleMobileDropdown = (dropdown: string): void => {
  mobileActiveDropdown.value = mobileActiveDropdown.value === dropdown ? null : dropdown;
};

const signInUrl = computed((): string => {
  return `${DASHBOARDS_URL}/merchant/auth/sign-in`;
});
</script>

<style scoped>
.custom-shadow {
  @apply shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08),0px_4px_6px_-2px_rgba(16,24,40,0.03)];
}
.hover-green-bg:hover {
  @apply bg-[rgba(115,226,163,0.1)];
}
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
